import { createSlice } from '@reduxjs/toolkit';

import {
  initialChannel,
  initialChannelBookmark,
  initialHoveredChannel,
  initialPermission,
} from './constants/initialStates';
import { OWNER_PERMISSIONS } from './constants/ownerPermissions';
import { REQUEST } from '../../constants/request';
import { INITIAL_LOADING_STATE } from '../../utils/asyncThunkRequest';

import { createChannelExtraReducers } from './asyncThunk/createChannel';
import { fetchChannelExtraReducers } from './asyncThunk/fetchChannel';
import { updateChannelExtraReducers } from './asyncThunk/updateChannel';
import { fetchAllChannelsExtraReducers } from './asyncThunk/fetchAllChannels';
import { fetchTrendingChannelsExtraReducers } from './asyncThunk/fetchTrendingChannels';
import { fetchTrendingUsersExtraReducers } from './asyncThunk/fetchTrendingUsers';
import { fetchStreamChannelsExtraReducers } from './asyncThunk/fetchStreamChannels';
import { fetchHoveredChannelExtraReducers } from './asyncThunk/fetchHoveredChannel';
import { addChannelBookmarkExtraReducers } from './asyncThunk/addChannelBookmark';
import { fetchChannelBookmarkExtraReducers } from './asyncThunk/fetchChannelBookmark';
import { removeChannelBookmarkExtraReducers } from './asyncThunk/removeChannelBookmark';
import { removeSubscriptionsExtraReducers } from './asyncThunk/removeSubscriptions';

import { generateChannelCIDKey } from '../../utils/generateChannelCIDKey';
import { generateStreamCIDKey } from '../Streams/utils/generateStreamCIDKey';

export { createChannel } from './asyncThunk/createChannel';
export { fetchChannel } from './asyncThunk/fetchChannel';
export { updateChannel } from './asyncThunk/updateChannel';
export { fetchAllChannels } from './asyncThunk/fetchAllChannels';
export { fetchTrendingChannels } from './asyncThunk/fetchTrendingChannels';
export { fetchStreamChannels } from './asyncThunk/fetchStreamChannels';
export { fetchHoveredChannel } from './asyncThunk/fetchHoveredChannel';
export { addChannelBookmark } from './asyncThunk/addChannelBookmark';
export { fetchChannelBookmark } from './asyncThunk/fetchChannelBookmark';
export { removeChannelBookmark } from './asyncThunk/removeChannelBookmark';
export { removeSubscriptions } from './asyncThunk/removeSubscriptions';

const INITIAL_STATE = {
  channels: {
    channel: {
      byId: {},
      allIds: [],
      /*
      '$orgId/$streamId/$channelId}': {
        loading: REQUEST.idle,
        currentRequestId: undefined,
        error: null,
      },
      */
      request: {},
      actions: {
        create: {
          request: {},
        },

        update: {
          request: {},
        },
        removeSubscription: {
          request: {},
        },
      },
    },
    users: {
      byChannel: {},
      request: {},
    },
    displayTrendingUsers: {
      /*
        default user channel
        ["g/$userId/public"]
      */
      layout: [],
      request: {
        ...INITIAL_LOADING_STATE,
      },
    },
    displayTrendingChannels: {
      /*
      ["$orgId/$streamId/$channelId"]
      */
      layout: [],
      request: { ...INITIAL_LOADING_STATE },
    },
    /*
    fetch fetchAllChannels extraReducer
    */
    displayChannels: {
      /*
      ["$orgId/$streamId/$channelId"]
      */
      layout: [],
      request: { ...INITIAL_LOADING_STATE },
    },
    hoveredChannels: {
      byId: {},
      allIds: [],
      request: {},
    },
    // fetch fetchStreamChannels extraReducer
    stream: {
      /*
     'g/1qwertyuiop': ['public', 'private', 'admin']
      */
      byStream: {},
      request: {},
    },
    bookmark: {
      byId: {},
      allIds: [],
      request: {},
    },
    deactivatedItems: {
      byId: {},
      allIds: [],
      request: {},
    },
    archivedItems: {
      byId: {},
      allIds: [],
      request: {},
    },
  },
};

export const ChannelsSlice = createSlice({
  name: 'channels',
  initialState: INITIAL_STATE,
  reducers: {
    getResetChannelsLayout: state => {
      state.channels.displayChannels.layout = [];
    },
    setSelectedTags: (state, { payload }) => {
      state.channels.bookmark.byId[payload.id].selectedTags = [
        ...payload.selectedTags,
      ];
    },
    selectChannelPrimaryUseCase: (state, { payload }) => {
      state.channels.channel.byId[payload.id].primaryUseCase = payload.useCase;
    },
    setDefaultOwnerPermissions: state => {
      state.permissions = {
        ...state.permissions,
        timeline: {
          loading: REQUEST.idle,
          currentRequestId: undefined,
          userRoleMapping: {
            userId: '',
            roles: ['owner'],
            mapping: OWNER_PERMISSIONS,
          },
        },
      };
    },
    setDeactivated: (state, { payload }) => {
      state.channels.deactivatedItems.byId[payload.id] = payload.status;
    },
    setArchivedChannel: (state, { payload }) => {
      state.channels.archivedItems.byId[payload.id] = payload.value;
    },
    resetChannelsSlice: () => INITIAL_STATE,
    resetChannelRequest: (state, { payload }) => {
      delete state.channels.channel.request[
        generateChannelCIDKey(payload.path)
      ];
    },
    resetStreamChannelsRequest: (state, { payload }) => {
      delete state.channels.stream.request[generateStreamCIDKey(payload.path)];
    },
  },
  extraReducers: {
    ...createChannelExtraReducers,
    ...fetchChannelExtraReducers,
    ...updateChannelExtraReducers,
    ...fetchStreamChannelsExtraReducers,
    ...fetchAllChannelsExtraReducers,
    ...fetchTrendingChannelsExtraReducers,
    ...fetchTrendingUsersExtraReducers,
    ...fetchHoveredChannelExtraReducers,
    ...fetchChannelBookmarkExtraReducers,
    ...removeSubscriptionsExtraReducers,
    ...addChannelBookmarkExtraReducers,
    ...removeChannelBookmarkExtraReducers,
  },
});

export const {
  getResetChannelsLayout,
  setSelectedTags,
  selectChannelPrimaryUseCase,
  setDefaultOwnerPermissions,
  setDeactivated,
  setArchivedChannel,
  setArchivedChannels,
  resetChannelsSlice,
  resetChannelRequest,
  resetStreamChannelsRequest,
} = ChannelsSlice.actions;

export default ChannelsSlice.reducer;

export const selectorGetChannelBookmark = channelCIDKey => state =>
  state.channels.channels.bookmark.byId[channelCIDKey] ||
  initialChannelBookmark;
export const selectorUserRoleMapping = channelCIDKey => state =>
  state.channels.channels.channel.byId[channelCIDKey]?.userData
    ?.userRoleMapping || initialPermission.userRoleMapping;
export const selectorChannelPermission = (state, channelCIDKey) =>
  state.channels.channels.channel.byId?.[channelCIDKey]?.userData ||
  initialPermission;
export const selectorTrendingChannelsLayout = state =>
  state.channels.channels.displayTrendingChannels.layout;
export const selectorTrendingChannelsLoadingState = state =>
  state.channels.channels.displayTrendingChannels.request.loading;
export const selectorChannelLoading = (state, channelCIDKey) =>
  state.channels.channels.channel.request[channelCIDKey]?.loading ||
  REQUEST.idle;
export const selectorGetActionsLoadingStatus = (state, channelCIDKey, action) =>
  state.channels.channels.channel.actions[action].request[channelCIDKey]
    ?.loading || REQUEST.idle;
export const selectorGetChannelError = channelUID => state =>
  state.channels.channels.channel.request[channelUID]?.error || null;
export const selectorByIdChannels = state => state.channels.channels.byId || {};
export const selectorGetChannel = channelUID => state =>
  state.channels.channels.channel.byId[channelUID] || initialChannel;
export const selectorGetChannelNew = (state, channelUID) =>
  state.channels.channels.channel.byId[channelUID] || initialChannel;
export const selectorGetHoveredChannel = channelUID => state =>
  state.channels.channels.hoveredChannels.byId[channelUID] ||
  initialHoveredChannel;
export const selectorGetChannelRequest = (state, channelUID) =>
  state.channels.channels.channel.request[channelUID] || {
    ...INITIAL_LOADING_STATE,
  };
export const selectorGetHoveredChannelRequest = (state, channelUID) =>
  state.channels.channels.hoveredChannels.request[channelUID] || {
    ...INITIAL_LOADING_STATE,
  };
export const selectorStreamChannelList = (state, streamUID) =>
  state.channels.channels.stream.byStream[streamUID] || [];
export const selectorStreamChannelListRequest = (state, streamUID) =>
  state.channels.channels.stream.request[streamUID] || {
    ...INITIAL_LOADING_STATE,
  };
export const selectorChannelHash = uid => state =>
  state.channels.channels.channel.byId[uid]?.metadata?.itemsHash || '';
export const selectorAllChannelsLayout = state =>
  state.channels.channels.displayChannels.layout || [];
export const selectorAllChannelsLoadingState = state =>
  state.channels.channels.displayChannels.request.loading;
export const selectorChannelArchivedItem = (state, channelCIDKey) =>
  state.channels.channels.archivedItems.byId[channelCIDKey];
export const selectorChannelDeactivatedItem = (state, channelCIDKey) =>
  state.channels.channels.deactivatedItems.byId[channelCIDKey];
export const selectorGetChannelUsers = (state, channelCIDKey) =>
  state.channels.channels.users.byChannel[channelCIDKey] || [];
export const selectorGetChannelUsersLoadingStatus = (state, channelCIDKey) =>
  state.channels.channels.users.request[channelCIDKey]?.loading || REQUEST.idle;
export const selectorGetTrendingUsers = state =>
  state.channels.channels.displayTrendingUsers.layout || [];
export const selectorGetTrendingUsersLoadingStatus = state =>
  state.channels.channels.displayTrendingUsers.request.loading;
export const selectorGetChannelProfilePicture = (state, channelCIDKey) =>
  state.channels.channels.channel.byId[channelCIDKey]?.metadata?.profilePicUrl;
